/**
 * File-picker form field that uploads to R2 via `POST /api/v1/content/uploads`
 * and exposes the resulting public URL via `value` / `onChange`. Tracks its own
 * upload-in-flight state and surfaces error messages inline.
 *
 * Designed to slot in where a plain `<Input type="url">` for `image_url` lived
 * before — same value contract (string URL or empty string).
 */
import { useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useUploadContentImage } from "@/hooks/use-content";
import { ApiError } from "@/lib/api-client";

const ACCEPT_MIME = "image/jpeg,image/png,image/gif,image/webp";

export interface ImageUploadFieldProps {
  /** Current public URL (empty string when no image). */
  value: string;
  /** Called with the new public URL after a successful upload, or "" when cleared. */
  onChange: (url: string) => void;
  /** Visible label above the field. */
  label: string;
  /** When true, render the asterisk + required hint. */
  required?: boolean;
  /** Optional helper line below the field (overrides the default). */
  helperText?: string;
  /** Disable interactions (e.g. while parent form is pending). */
  disabled?: boolean;
}

export function ImageUploadField({
  value,
  onChange,
  label,
  required = false,
  helperText,
  disabled = false,
}: ImageUploadFieldProps) {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadContentImage();
  const [localError, setLocalError] = useState<string | null>(null);

  function handleFile(file: File) {
    setLocalError(null);
    upload.mutate(file, {
      onSuccess: (res) => onChange(res.url),
      onError: (err) => {
        const msg =
          err instanceof ApiError ? err.payload.message : "Upload failed. Try a different image.";
        setLocalError(msg);
      },
    });
  }

  function handleClear() {
    onChange("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setLocalError(null);
  }

  const defaultHelper = required
    ? "Required for Instagram. JPG, PNG, GIF, or WEBP up to 8 MB."
    : "Optional. JPG, PNG, GIF, or WEBP up to 8 MB. Facebook attaches the image; LinkedIn and Twitter ignore it for now.";

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId}>
        {label}{" "}
        {required ? (
          <span className="text-red-500">*</span>
        ) : (
          <span className="text-muted-foreground">(optional)</span>
        )}
      </Label>

      {value ? (
        <div className="flex flex-col gap-2">
          {/* biome-ignore lint/performance/noImgElement: external R2 URL preview */}
          <img
            src={value}
            alt="Uploaded preview"
            className="max-h-48 w-auto rounded border border-gray-200 object-contain"
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || upload.isPending}
            >
              Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={disabled || upload.isPending}
              className="text-destructive hover:text-destructive"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || upload.isPending}
          className="w-fit"
        >
          {upload.isPending ? "Uploading…" : "Choose image"}
        </Button>
      )}

      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={ACCEPT_MIME}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
        className="hidden"
      />

      <p className="text-xs text-muted-foreground">{helperText ?? defaultHelper}</p>

      {localError && <p className="text-xs text-destructive">{localError}</p>}
    </div>
  );
}
